import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../store/useStore";
import { createCheckout, getOrderStatus, cancelOrder } from "../lib/api";
import { ProgressStubs } from "../components/TicketStub";
import { cartTotal } from "../store/useStore";
import { t } from "../lib/i18n";
import { LANGUAGES } from "../data/languages";

const STEPS = ["step_1","step_2","step_3","step_4"] as const;
const STEP_ICONS = ["👉","💛","💳","🟢"];

const PAY_IDLE_MS = 4 * 60_000;
const FAIL_IDLE_MS = 90_000;   // fail/unknown screens auto-return to attract
// "unknown" = we could not confirm the payment either way — the card may have
// been charged. Never tell the guest "no charge" in that state.
type FailReason = "canceled" | "expired" | "rejected" | "network" | "unknown" | null;

export default function TerminalGuide() {
  const lang = useStore((s) => s.language);
  const cart = useStore((s) => s.cart);
  const customerName = useStore((s) => s.customerName);
  const setOrderId = useStore((s) => s.setOrderId);
  const orderId = useStore((s) => s.orderId);
  const setPaymentStatus = useStore((s) => s.setPaymentStatus);
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [fail, setFail] = useState<FailReason>(null);
  const [showHelp, setShowHelp] = useState(false);
  const cancelTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orderIdRef = useRef<string | null>(null);
  const total = cartTotal(cart);
  const isRtl = LANGUAGES.find((l) => l.code === lang)?.rtl;

  const startOrder = async () => {
    setFail(null);
    setStepIdx(0);
    try {
      const r = await createCheckout({ cart, customerName, language: lang || "es" });
      setOrderId(r.order_id);
      orderIdRef.current = r.order_id;
    } catch { setFail("network"); }
  };

  useEffect(() => { startOrder(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    cancelTimer.current = setTimeout(async () => {
      // Final authoritative check before claiming anything — the guest may
      // have completed the payment seconds ago while polling lagged.
      const id = orderIdRef.current;
      if (!id) { setFail("expired"); return; }
      try {
        const s = await getOrderStatus(id);
        if (s.status === "processed") { navigate("/kiosk/thanks"); return; }
        if (["canceled", "expired", "rejected"].includes(s.status)) { setFail(s.status as FailReason); return; }
        // Still non-final after 4 minutes: cancel best-effort, but we cannot
        // prove the card wasn't charged — send the guest to the bar.
        cancelOrder(id).catch(() => {});
        setFail("unknown");
      } catch {
        setFail("unknown");
      }
    }, PAY_IDLE_MS);
    return () => { if (cancelTimer.current) clearTimeout(cancelTimer.current); };
  }, [orderId, navigate]);

  // The idle reset in App.tsx is suspended on this screen; once we land on a
  // terminal fail/unknown state, return to attract ourselves so a walked-away
  // guest doesn't leave the kiosk wedged.
  const resetStore = useStore((s) => s.reset);
  useEffect(() => {
    if (!fail) return;
    const tm = setTimeout(() => { resetStore(); window.location.assign("/kiosk"); }, FAIL_IDLE_MS);
    return () => clearTimeout(tm);
  }, [fail, resetStore]);

  useEffect(() => {
    if (!orderId || fail) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const s = await getOrderStatus(orderId);
        setPaymentStatus(s.status as any);
        if (s.status === "at_terminal") setStepIdx((i) => Math.max(i, 1));
        if (s.status === "processed") { navigate("/kiosk/thanks"); return; }
        if (["canceled","expired","rejected"].includes(s.status)) { setFail(s.status as FailReason); return; }
      } catch {}
      setTimeout(tick, 1500);
    };
    tick();
    return () => { cancelled = true; };
  }, [orderId, fail, navigate, setPaymentStatus]);

  useEffect(() => {
    if (stepIdx < 1 || fail) return;
    const tm = setTimeout(() => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1)), 4000);
    return () => clearTimeout(tm);
  }, [stepIdx, fail]);

  const tryAgain = async () => {
    if (orderIdRef.current) cancelOrder(orderIdRef.current).catch(() => {});
    setOrderId(null);
    orderIdRef.current = null;
    await startOrder();
  };
  const editOrder = () => {
    if (orderIdRef.current) cancelOrder(orderIdRef.current).catch(() => {});
    setOrderId(null);
    navigate("/kiosk/menu");
  };

  if (showHelp) {
    return (
      <div className="grain min-h-screen flex flex-col items-center justify-center bg-paper p-8 text-center" dir={isRtl ? "rtl" : undefined}>
        <p className="text-7xl mb-4">🙋</p>
        <p className="font-hand text-3xl text-mezcal">— {t(lang, "need_help")} —</p>
        <h1 className="font-display font-black text-6xl mt-2 max-w-2xl tracking-kerned leading-tight">{t(lang, "come_to_bar_title")}</h1>
        <p className="font-sans text-xl text-ink/60 mt-3">{t(lang, "come_to_bar_sub")}</p>
        <button onClick={() => setShowHelp(false)} className="mt-12 px-10 py-4 rounded-sm bg-ink text-paper font-display font-bold">
          ← {t(lang, "guide_help_back")}
        </button>
      </div>
    );
  }

  if (fail === "unknown") {
    // Payment state unconfirmed — no "no charge" claim, no retry (a second
    // order here is the double-charge path). Staff resolves at the bar.
    return (
      <div className="grain min-h-screen flex flex-col items-center justify-center bg-paper p-8 text-center" dir={isRtl ? "rtl" : undefined}>
        <p className="text-7xl mb-4">🙋</p>
        <p className="font-hand text-3xl text-mezcal">— {t(lang, "need_help")} —</p>
        <h1 className="font-display font-black text-6xl mt-2 max-w-2xl tracking-kerned leading-tight">{t(lang, "come_to_bar_title")}</h1>
        <p className="font-sans text-xl text-ink/60 mt-3">{t(lang, "come_to_bar_sub")}</p>
        <div className="mt-10 font-mono text-xs tracking-widest text-ink/50">
          {t(lang, "guide_order")} · {orderIdRef.current ? orderIdRef.current.slice(-6).toUpperCase() : "·····"}
        </div>
      </div>
    );
  }

  if (fail) {
    const failKey = ({
      canceled: "fail_canceled", expired: "fail_expired",
      rejected: "fail_rejected", network: "fail_network",
    } as const)[fail];
    return (
      <div className="grain min-h-screen flex flex-col items-center justify-center bg-paper p-8 text-center" dir={isRtl ? "rtl" : undefined}>
        <p className="font-hand text-3xl text-cactus">— {t(lang, "fail_caption")} —</p>
        <h1 className="font-display font-black text-6xl mt-2 max-w-2xl tracking-kerned leading-tight">
          {t(lang, failKey)}
        </h1>
        <p className="font-sans text-xl text-ink/60 mt-4">{t(lang, "fail_no_charge")}</p>

        <div className="mt-12 grid grid-cols-2 gap-4 w-full max-w-xl">
          <button onClick={editOrder} className="py-5 rounded-sm border-2 border-ink/40 hover:border-ink font-display font-bold text-lg">
            {t(lang, "fail_edit")}
          </button>
          <button onClick={tryAgain} className="py-5 rounded-sm bg-mezcal text-paper font-display font-bold text-lg">
            {t(lang, "fail_try_again")}
          </button>
        </div>
        <button onClick={() => setShowHelp(true)} className="mt-8 font-mono text-xs tracking-widest text-ink/50 underline">
          {t(lang, "need_help")}
        </button>
      </div>
    );
  }

  const stepKey = STEPS[stepIdx];
  const stepIcon = STEP_ICONS[stepIdx];

  return (
    <div className="grain min-h-screen flex flex-col bg-ink text-paper p-10 relative" dir={isRtl ? "rtl" : undefined}>
      <header className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] tracking-[0.4em] text-mango uppercase">{t(lang, "guide_in_progress")}</p>
          <h2 className="font-display font-black text-2xl mt-1">{customerName}</h2>
        </div>
        <ProgressStubs total={STEPS.length} current={stepIdx} />
      </header>

      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <div className="text-[10rem] leading-none mb-6 animate-breathe">{stepIcon}</div>
        <p className="font-mono text-xs tracking-[0.4em] text-mango uppercase">
          {t(lang, "guide_step_word")} {stepIdx + 1} / {STEPS.length}
        </p>
        <h1 className="font-display font-black text-7xl mt-3 max-w-3xl tracking-kerned leading-[0.95]">
          {t(lang, stepKey)}
        </h1>
      </div>

      <footer className="flex items-end justify-between">
        <div className="font-mono text-xs tracking-widest text-paper/50">
          {t(lang, "guide_order")} · {orderId ? orderId.slice(-6).toUpperCase() : "·····"}
        </div>
        <div className="font-display text-3xl font-bold tabular-nums text-mango">${total}</div>
        <div className="flex gap-5 text-paper/60 font-mono text-xs tracking-widest">
          <button onClick={editOrder} className="underline">{t(lang, "guide_cancel")}</button>
          <span>·</span>
          <button onClick={() => setShowHelp(true)} className="underline">{t(lang, "guide_help")}</button>
        </div>
      </footer>
    </div>
  );
}
